import {
  PaperClipOutlined
} from '@ant-design/icons';
import {
  EAvatarSize,
  KAvatar,
  KMdText,
  KMdText700,
  KMdText700PrimaryBase,
  KSemiText700,
  KSmTextSecondary65,
  KTag,
} from "@components";
import { useKContext } from '@context';
import {
  ECardType,
  IAttachment,
  ICombinedCard,
  IItemCardProps,
  IParams,
} from "@types";
import { formatDate, getLabelOfField, ignoreFields } from '@utils/index';
import { Divider, Flex, FlexProps, Space } from "antd";
import { FC, ReactNode, useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import {
  AllergyItem,
  AppointmentItem,
  ConditionItem,
  FilesItem,
  HospitalizationItem,
  IdCardItem,
  MedicationItem,
  NoteItem,
  PractitionerItem,
  ProcedureItem,
  QuestionItem,
  ToDoItem,
  VaccinationItem,
} from "./item";
import { FolderOpenOutlinedIcon } from '@assets/icons';
import { TestResultsItem } from './item/TestResultsItem';

export const CustomSpaceList = ({
  children,
  space = 8,
}: {
  children: React.ReactNode;
  space?: number;
}) => (
  <Space direction="vertical" size={space} style={{ width: "100%", height: "max-content" }}>
    {children}
  </Space>
);

interface IWrapperCardList extends FlexProps {
  space?: number;
}

export const WrapperCardList = ({
  children,
  space = 8,
  ...props
}: IWrapperCardList) => (
  <Flex
    vertical
    gap={space}
    style={{
      overflowY: "auto",
      maxHeight: "100vh",
    }}
    {...props}
  >
    {children}
  </Flex>
);

interface ISectionProps {
  title: string;
  children: ReactNode;
}

export const Section = ({ children, title }: ISectionProps) => (
  <Flex gap={8} vertical>
    <KSemiText700>{title}</KSemiText700>
    <Flex gap={8} vertical>
      {children}
    </Flex>
  </Flex>
);

const MAX_TOPICS_TO_SHOW = 2

export const MetadataDetail: FC<ICombinedCard & { showTopics?: boolean }> = ({
  updatedBy,
  updatedTime,
  topics,
  showTopics
}) => {
  const time = useMemo(() => {
    const timestamp = updatedTime;
    return formatDate(timestamp, 'MONTH_DAY_WITH_SHORT_YEAR_AND_TIME_AM_PM')
  }, [updatedTime]);

  return (
    <>
      <Divider />
      {(showTopics && !!topics.length) && (
        <Flex gap={8} align="center" wrap="wrap">
          {topics.slice(0, MAX_TOPICS_TO_SHOW).map((i: any) => (
            <Flex key={i._id} gap={4} align="center">
              <FolderOpenOutlinedIcon style={{ fontSize: 16 }} />
              <KMdText700PrimaryBase>{i.title}</KMdText700PrimaryBase>
            </Flex>
          ))}
          {topics.length > MAX_TOPICS_TO_SHOW && (
            <KMdText700PrimaryBase>
              +{topics.length - MAX_TOPICS_TO_SHOW}
            </KMdText700PrimaryBase>
          )}
        </Flex>
      )}

      <Flex gap={4} align='center'>
        <KAvatar
          size={EAvatarSize.small}
          shape='circle'
          firstName={updatedBy?.firstName}
          lastName={updatedBy?.lastName}
          imgUrl={updatedBy?.avatar}
        />
        <KSmTextSecondary65>
          {updatedBy?.firstName} • {time}
        </KSmTextSecondary65>
      </Flex>
    </>
  );
};

interface ICardItemContainer extends FlexProps {
  card?: any;
  showMetadata?: boolean;
  showTopics?: boolean;
};

export const CardItemContainer: FC<ICardItemContainer> = ({
  card,
  children,
  showMetadata = false,
  showTopics = false,
  ...props
}) => {
  const { cardId } = useParams<IParams>();
  const { setCardTypeSelected } = useKContext();
  const { matchFields, attachments, cardType, _id } = card || {};
  useEffect(() => {
    if (_id !== cardId) return;
    setCardTypeSelected(cardType);
  }, [cardType, _id, cardId]);

  return (
    <Flex
      style={{ maxWidth: "100%" }}
      gap={8}
      vertical
      justify="space-between"
      flex={1}
      {...props}
    >
      {children}
      {matchFields?.length > 0 &&
        matchFields.map(
          (field: keyof ICombinedCard, idx: number) =>
            !ignoreFields.includes(field) && (
              <Flex gap={4} key={idx}>
                <KMdText700>{getLabelOfField(cardType, field)}:</KMdText700>
                <KMdText $ellipsis>{card[field]}</KMdText>
              </Flex>
            )
        )}
      {(showMetadata && (!!attachments?.length)) && (
        <Flex wrap="wrap" gap={4}>
          {attachments.map((atm: IAttachment) => (
            <KTag key={atm._id} icon={<PaperClipOutlined />}>
              {atm.originalName}
            </KTag>
          ))}
        </Flex>)
      }
      {showMetadata && <MetadataDetail showTopics={showTopics} {...card} />}
    </Flex>
  );
};

export const cardTypeToCardItem: {
  [key: string]: { CardItem: React.ComponentType<IItemCardProps<any>> };
} = {
  [ECardType.Allergies]: { CardItem: AllergyItem },
  [ECardType.Appointments]: { CardItem: AppointmentItem },
  [ECardType.Conditions]: { CardItem: ConditionItem },
  [ECardType.Files]: { CardItem: FilesItem },
  [ECardType.Hospitalizations]: { CardItem: HospitalizationItem },
  [ECardType.IdCard]: { CardItem: IdCardItem },
  [ECardType.Medications]: { CardItem: MedicationItem },
  [ECardType.Notes]: { CardItem: NoteItem },
  [ECardType.Practitioners]: { CardItem: PractitionerItem },
  [ECardType.Procedures]: { CardItem: ProcedureItem },
  [ECardType.Questions]: { CardItem: QuestionItem },
  [ECardType.Vaccinations]: { CardItem: VaccinationItem },
  [ECardType.ToDos]: { CardItem: ToDoItem },
  [ECardType.TestResults]: { CardItem: TestResultsItem },
};
